export type HTTPQueryValue = string | number | boolean

/**
 * Immutable value object representing an HTTP query string.
 *
 * Produced by query builders (e.g. {@link ODataQueryBuilder}) via their
 * `build()` method. Holds a list of already-encoded `key=value` pairs so that
 * the encoding produced by the builder is preserved verbatim through merges
 * and serialization — no re-encoding surprises.
 *
 * Instances are immutable: {@link with} and {@link merge} return new
 * instances rather than mutating.
 */
export class HTTPQuery {
  private readonly pairs: readonly string[]

  /**
   * Construct an {@link HTTPQuery} from any of:
   *
   * - **nothing** — an empty query.
   * - **string** — a query string like `"?a=1&b=2"`, `"a=1&b=2"`, or `""`.
   *   Pairs are taken verbatim; no re-encoding is applied.
   * - **record** — `{ key: value }` map. Each entry is URL-encoded via
   *   the same rules as {@link with}; `undefined`/`null` values are skipped.
   * - **string[]** — pre-encoded `key=value` pairs (used internally by
   *   {@link with} and {@link merge}).
   */
  constructor(
    input: string | Record<string, HTTPQueryValue | undefined | null> | readonly string[] = []
  ) {
    if (typeof input === "string") {
      const stripped = input.startsWith("?") ? input.slice(1) : input
      this.pairs = stripped ? stripped.split("&") : []
    } else if (Array.isArray(input)) {
      this.pairs = input as readonly string[]
    } else {
      const record = input as Record<string, HTTPQueryValue | undefined | null>
      const pairs: string[] = []
      for (const key of Object.keys(record)) {
        const value = record[key]
        if (value === undefined || value === null) continue
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      }
      this.pairs = pairs
    }
  }

  /** `true` when there are no params. */
  get isEmpty(): boolean {
    return this.pairs.length === 0
  }

  /**
   * Return a new {@link HTTPQuery} with `key=value` appended. Passing
   * `undefined` or `null` as value is a no-op — handy for conditional extras:
   *
   * ```ts
   * qb.build().with("deep", deep ? true : null)
   * ```
   *
   * Key and value are URL-encoded.
   */
  with(key: string, value: HTTPQueryValue | undefined | null): HTTPQuery {
    if (value === undefined || value === null) return this
    const pair = `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    return new HTTPQuery([...this.pairs, pair])
  }

  /**
   * Return a new {@link HTTPQuery} combining this one with another.
   * Pairs are appended in order; no collision resolution is performed —
   * duplicate keys remain duplicates (matching standard URL-query semantics).
   */
  merge(other: HTTPQuery): HTTPQuery {
    if (other.isEmpty) return this
    if (this.isEmpty) return other
    return new HTTPQuery([...this.pairs, ...other.pairs])
  }

  /**
   * Serialize to a URL query string, including the leading `?`.
   * Returns `""` when empty.
   */
  toString(): string {
    return this.pairs.length === 0 ? "" : `?${this.pairs.join("&")}`
  }
}
